import "@/App.css";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Toaster } from "sonner";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CustomerAuthProvider } from "@/context/CustomerAuthContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { useCurrency } from "@/context/CurrencyContext";
import { X } from "lucide-react";
import { GOOGLE_CLIENT_ID } from "@/lib/api";
import { AnnouncementBar } from "@/components/AnnouncementBar";
import { SaleCountdownBar } from "@/components/SaleCountdownBar";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CartDrawer } from "@/components/CartDrawer";
import { NewsletterPopup } from "@/components/NewsletterPopup";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import CartPage from "@/pages/CartPage";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Contact from "@/pages/Contact";
import InfoPage from "@/pages/InfoPage";
import Wishlist from "@/pages/Wishlist";
import SharedWishlist from "@/pages/SharedWishlist";
import Offers from "@/pages/Offers";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Account from "@/pages/Account";
import Unsubscribe from "@/pages/Unsubscribe";
import AdminLogin from "@/pages/AdminLogin";
import Admin from "@/pages/Admin";

const INFO_ROUTES = ["/orders-returns", "/shipping", "/size-guide", "/faqs", "/privacy-policy", "/terms"];

const ScrollToTop = () => {
  const location = useLocation();
  // Scroll to top on any navigation (covers different routes)
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname, location.key]);

  // Also handle clicking a link to the page you're already on
  useEffect(() => {
    const handler = (e) => {
      const a = e.target.closest("a[href]");
      if (!a) return;
      let url;
      try {
        url = new URL(a.href, window.location.origin);
      } catch {
        return;
      }
      if (url.origin === window.location.origin &&
          url.pathname === window.location.pathname &&
          url.search === window.location.search) {
        if (window.__lenis) window.__lenis.scrollTo(0);
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  return null;
};

const RequireAdmin = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading...</div>;
  if (user === false) return <Navigate to="/admin/login" replace />;
  return children;
};

export const CurrencyNotice = () => {
  const { currency, isConverted, meta } = useCurrency();
  const [show, setShow] = useState(() => sessionStorage.getItem("zebaa_fx_notice_dismissed") !== "1");
  if (!isConverted || !show) return null;
  const symbol = meta[currency]?.symbol || currency;
  return (
    <div className="bg-charcoal text-white/90 text-[11px] sm:text-xs" data-testid="currency-notice">
      <div className="max-w-[1800px] mx-auto px-4 py-1.5 flex items-center justify-center gap-3 text-center">
        <span>Prices shown in {symbol} {currency} — you'll be charged in GBP (£).</span>
        <button
          onClick={() => { setShow(false); sessionStorage.setItem("zebaa_fx_notice_dismissed", "1"); }}
          data-testid="currency-notice-dismiss"
          className="text-white/50 hover:text-white transition-colors shrink-0"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
};

const Storefront = () => {
  const location = useLocation();
  return (
    <>
      <AnnouncementBar />
      <div className="sticky top-0 z-50">
        <SaleCountdownBar />
        <Navbar />
      </div>
      <CurrencyNotice />
      <CartDrawer />
      <NewsletterPopup />
      <AnimatePresence mode="wait">
        <motion.main
          key={location.pathname}
          className="min-h-[70vh]"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <Routes location={location}>
            <Route path="/" element={<Home />} />
            <Route path="/shop" element={<Navigate to="/shop/all" replace />} />
            <Route path="/shop/:category" element={<Shop />} />
            <Route path="/product/:slug" element={<ProductDetail />} />
            <Route path="/wishlist" element={<Wishlist />} />
            <Route path="/wishlist/shared/:token" element={<SharedWishlist />} />
            <Route path="/offers" element={<Offers />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/order-confirmation/:orderNumber" element={<OrderConfirmation />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/account" element={<Account />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            {INFO_ROUTES.map((p) => <Route key={p} path={p} element={<InfoPage />} />)}
          </Routes>
        </motion.main>
      </AnimatePresence>
      <Footer />
    </>
  );
};

function App() {
  useEffect(() => {
    // Always start at the top on a fresh load / refresh (esp. on mobile, where the
    // browser would otherwise restore the previous scroll position).
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    window.scrollTo(0, 0);

    // Smooth (Lenis) scrolling ONLY on true desktop: a device with a fine pointer,
    // real hover, and a wide viewport. Phones/tablets use native momentum scrolling,
    // which avoids the "teleporting up/down" jitter Lenis causes on touch.
    const isDesktop = window.matchMedia("(hover: hover) and (pointer: fine)").matches
      && !("ontouchstart" in window)
      && (navigator.maxTouchPoints || 0) === 0
      && window.innerWidth >= 1024;
    if (!isDesktop) {
      window.__lenis = null;
      return;
    }
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true, smoothTouch: false, syncTouch: false });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  const tree = (
    <AuthProvider>
      <CustomerAuthProvider>
        <CurrencyProvider>
        <CartProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<RequireAdmin><Admin /></RequireAdmin>} />
              <Route path="/*" element={<Storefront />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="bottom-right" richColors />
        </CartProvider>
        </CurrencyProvider>
      </CustomerAuthProvider>
    </AuthProvider>
  );

  return (
    <div className="App">
      {GOOGLE_CLIENT_ID ? (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>{tree}</GoogleOAuthProvider>
      ) : (
        tree
      )}
    </div>
  );
}

export default App;
